import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getKitsConProductos, getMisSuscripciones } from '../../lib/portalClienteApi'
import CheckoutFlow from '../../components/CheckoutFlow'

/** Mapeo segmento de hogar → códigos de kits que aplican (igual que NuevaSuscripcion). */
const SEGMENTOS = [
  { id: 'solo',           titulo: 'Vivo solo o en pareja', sub: '1 a 2 personas',                icono: '👤', detalle: 'Lo justo para mantener la casa al día.', kits: ['solo', 'duo'] },
  { id: 'familia',        titulo: 'Familia tipo',          sub: '3 a 4 personas',                icono: '👨‍👩‍👧', detalle: 'Cubre cocina, baño y limpieza general.',     kits: ['familiar', 'confort'] },
  { id: 'familia_grande', titulo: 'Familia grande',        sub: '5+ personas · perro/gato',     icono: '🏡', detalle: 'Más cantidad y más variedad.',               kits: ['hogar_plus', 'exclusive'] },
  { id: 'comercio',       titulo: 'Comercio u oficina',    sub: 'Almacén, bar, estudio',         icono: '🏢', detalle: 'Productos de uso intensivo.',                kits: ['comercio_local', 'estudio_oficina'] },
]

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }

export default function ProbarKit() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()

  const [paso, setPaso] = useState(0)  // 0 segmento · 1 kit · 2 checkout
  const [kits, setKits] = useState([])
  const [verTodos, setVerTodos] = useState(false)
  const [tierActivo, setTierActivo] = useState('essential')
  const [kitExpandido, setKitExpandido] = useState(null)

  const [segmentoId, setSegmentoId] = useState('')
  const [kitElegido, setKitElegido] = useState(null)
  const [pedidoCreado, setPedidoCreado] = useState(null)
  // Si el usuario YA es suscriptor activo, precargamos su dirección de siempre
  // para no obligarlo a re-escribir todo cuando quiere probar otro kit one-shot.
  const [direccionSusc, setDireccionSusc] = useState(null)

  useEffect(() => { getKitsConProductos().then(setKits) }, [])
  useEffect(() => {
    if (!user?.id) { setDireccionSusc(null); return }
    getMisSuscripciones(user.id).then(subs => {
      const activa = (subs || []).find(s => s.estado === 'activa')
      if (!activa) { setDireccionSusc(null); return }
      setDireccionSusc({
        barrioId: activa.barrio_obj?.id || null,
        direccion: activa.direccion || '',
        apto: activa.apto || '',
        tipoVivienda: activa.tipo_vivienda || '',
        lat: activa.lat ?? null,
        lng: activa.lng ?? null,
      })
    }).catch(() => setDireccionSusc(null))
  }, [user?.id])

  const segmento = SEGMENTOS.find(s => s.id === segmentoId)
  const kitsVisibles = useMemo(() => {
    if (verTodos) return kits
    if (!segmento) return []
    let f = kits.filter(k => segmento.kits.includes(k.codigo))
    if (segmentoId !== 'comercio') f = f.filter(k => k.tier === tierActivo)
    return f
  }, [kits, segmento, tierActivo, verTodos, segmentoId])

  async function logout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  const nombre = profile?.nombre?.split(' ')[0] || ''

  /* ──────── Pedido completado ──────── */
  if (pedidoCreado) {
    return (
      <div className="lp-app">
        <Topbar onLogout={logout} />
        <main className="pk-success">
          <div className="pk-success-card">
            <div className="pk-success-icon">✓</div>
            <h1>¡Pedido creado!</h1>
            <p>Número de pedido: <strong>{pedidoCreado.numero_orden}</strong></p>
            <p className="pk-success-msg">
              Te enviamos los datos para transferir por WhatsApp al <strong>{profile?.phone || 'número que tenemos'}</strong>.
              Apenas confirmemos el pago, tu kit entra a la cola de armado.
            </p>
            <div className="pk-success-actions">
              <Link to="/cliente" className="pk-btn-primary">Ir a Mi cuenta</Link>
              <Link to="/cliente/probar-kit" reloadDocument className="pk-btn-ghost">Pedir otro kit</Link>
            </div>
          </div>
        </main>
        <style>{styles}</style>
      </div>
    )
  }

  /* ──────── Paso 2: Checkout ──────── */
  if (paso === 2 && kitElegido) {
    return (
      <div className="lp-app">
        <Topbar onLogout={logout} />
        <CheckoutFlow
          tipo="compra_unica"
          kitCodigo={kitElegido.codigo}
          subtotalKit={Number(kitElegido.precio_unica)}
          esSuscriptor={!!direccionSusc}
          direccionSuscriptor={direccionSusc}
          tituloHero={`Probá ${kitElegido.nombre}`}
          onCancelar={() => setPaso(1)}
          onCompletado={(p) => setPedidoCreado(p)}
        />
        <style>{styles}</style>
      </div>
    )
  }

  /* ──────── Pasos 0-1: elegir kit ──────── */
  return (
    <div className="lp-app">
      <Topbar onLogout={logout} />

      <section className="pk-hero">
        <div className="pk-hero-pill">
          <span className="pk-hero-dot"></span>
          Pedido único · sin atadura mensual
        </div>
        <h1 className="pk-hero-h1">
          {paso === 0
            ? <>Probá un <span>kit</span> {nombre && `, ${nombre}`}</>
            : <>Elegí <span>tu kit</span></>
          }
        </h1>
        <p className="pk-hero-sub">
          {paso === 0
            ? 'Comprás un kit por única vez y lo recibís cuando pasamos por tu barrio. Sin suscripción.'
            : segmento && `Te mostramos los kits para ${segmento.titulo.toLowerCase()}.`}
        </p>
      </section>

      <main className="pk-main">
        {/* Stepper */}
        <div className="pk-stepper">
          {['Hogar', 'Kit', 'Pedido'].map((label, i) => (
            <div key={i} className={`pk-step ${paso === i ? 'active' : ''} ${paso > i ? 'done' : ''}`}>
              <div className="pk-step-num">{paso > i ? '✓' : i + 1}</div>
              <div className="pk-step-label">{label}</div>
            </div>
          ))}
        </div>

        {/* Paso 0: Segmento */}
        {paso === 0 && (
          <div className="pk-segs">
            {SEGMENTOS.map(s => {
              const elegido = segmentoId === s.id
              return (
                <button key={s.id} className={`pk-seg ${elegido ? 'is-elegido' : ''}`}
                  onClick={() => { setSegmentoId(s.id); setTierActivo(s.id === 'comercio' ? 'comercial' : 'essential'); setKitExpandido(null) }}>
                  <div className="pk-seg-icon">{s.icono}</div>
                  <div className="pk-seg-titulo">{s.titulo}</div>
                  <div className="pk-seg-sub">{s.sub}</div>
                  <div className="pk-seg-det">{s.detalle}</div>
                  {elegido && <div className="pk-seg-check">✓ Seleccionado</div>}
                </button>
              )
            })}
            <BtnNav onCancelar={() => navigate('/cliente')} onNext={() => setPaso(1)} disabled={!segmentoId} />
          </div>
        )}

        {/* Paso 1: Kit */}
        {paso === 1 && (
          <div className="pk-card">
            {segmentoId !== 'comercio' && !verTodos && (
              <>
                <div className="pk-tier-info">
                  <strong>Essential</strong>: máximo ahorro con calidad comprobada.<br/>
                  <strong>Premium</strong>: marcas top y aromas que se sienten — Cottonmax 2-en-1, CIF, papel doble hoja.
                </div>
                <div className="pk-toggle-wrap">
                  <div className="pk-toggle">
                    <button className={tierActivo === 'essential' ? 'on' : ''} onClick={() => setTierActivo('essential')}>ESSENTIAL</button>
                    <button className={tierActivo === 'premium' ? 'on' : ''} onClick={() => setTierActivo('premium')}>PREMIUM</button>
                  </div>
                </div>
              </>
            )}

            <div className="pk-kits">
              {kitsVisibles.map(k => {
                const expandido = kitExpandido === k.codigo
                return (
                  <div key={k.codigo} className="pk-kit">
                    {k.destacado && <div className="pk-kit-badge">⭐ Más popular</div>}
                    <div className="pk-kit-head">
                      <div className="pk-kit-titulo">
                        <h3>{k.nombre}</h3>
                        <span className={`pk-kit-tier tier-${k.tier}`}>{TIER_LABEL[k.tier]}</span>
                      </div>
                      <div className="pk-kit-meta">
                        {k.personas} · {k.productos.filter(p => !p.es_regalo).length} productos
                        {k.productos.some(p => p.es_regalo) && <span style={{ color: '#5BBDD8', fontWeight: 700 }}> + 🎁 regalo del mes</span>}
                      </div>
                      {k.descripcion && <p className="pk-kit-desc">{k.descripcion}</p>}
                    </div>
                    <div className="pk-kit-precio-row">
                      <div>
                        <div className="pk-kit-precio">${Number(k.precio_unica).toLocaleString('es-UY')}</div>
                        <div className="pk-kit-precio-sub">una sola vez · envío incluido si es tu día</div>
                      </div>
                      <button className="pk-kit-ver" onClick={() => setKitExpandido(expandido ? null : k.codigo)}>
                        {expandido ? 'Ocultar productos ▴' : 'Ver qué incluye ▾'}
                      </button>
                    </div>
                    {expandido && (
                      <ul className="pk-kit-productos">
                        {k.productos.filter(p => !p.es_regalo).map((p, i) => (
                          <li key={i}>
                            <span className="pk-kit-prod-cant">{p.cantidad}×</span>
                            <span className="pk-kit-prod-nom">{p.nombre}</span>
                            {p.presentacion && <span className="pk-kit-prod-pres"> · {p.presentacion}</span>}
                            {p.marca && <span className="pk-kit-prod-marca"> · {p.marca}</span>}
                          </li>
                        ))}
                        {k.productos.some(p => p.es_regalo) && (
                          <li style={{ background: 'linear-gradient(90deg, #fffbeb, #fef3c7)', padding: '6px 10px', borderRadius: 6, marginTop: 4, border: '1px dashed #5BBDD8' }}>
                            <span style={{ fontSize: 16 }}>🎁</span>
                            <span className="pk-kit-prod-nom" style={{ color: '#78350f', fontWeight: 700 }}> Regalo sorpresa del mes</span>
                            <span className="pk-kit-prod-pres" style={{ color: '#92400e' }}> · cambia cada mes</span>
                          </li>
                        )}
                      </ul>
                    )}
                    <button className="pk-kit-elegir" onClick={() => { setKitElegido(k); setPaso(2) }}>
                      Comprar este kit →
                    </button>
                  </div>
                )
              })}
            </div>

            <button className="pk-link-todos" onClick={() => setVerTodos(v => !v)}>
              {verTodos ? '← Volver a las recomendaciones' : 'Quiero ver todos los kits →'}
            </button>

            <BtnNav onCancelar={() => setPaso(0)} backLabel="← Volver" />
          </div>
        )}

        {/* CTA suscripción */}
        {paso === 1 && (
          <div className="pk-cta-susc">
            <div>
              <strong>💡 ¿Sabías?</strong>
              <p>Si en vez de comprar suelto te suscribís, el kit te queda más barato y te llega <strong>todos los meses</strong> sin que tengas que pensarlo.</p>
            </div>
            <Link to="/cliente/nueva-suscripcion" className="pk-cta-link">Ver suscripción →</Link>
          </div>
        )}
      </main>

      <style>{styles}</style>
    </div>
  )
}

function Topbar({ onLogout }) {
  return (
    <nav className="lp-nav">
      <div className="lp-nav-inner">
        <Link to="/cliente" className="lp-logo">
          <img src="/isotipo.png" alt="" className="lp-logo-iso" />
          <span>Lim<em>pack</em></span>
        </Link>
        <div className="lp-nav-actions">
          <Link to="/cliente" className="lp-nav-link">← Mi cuenta</Link>
          <button onClick={onLogout} className="lp-nav-logout">Salir</button>
        </div>
      </div>
    </nav>
  )
}

function BtnNav({ onCancelar, onNext, disabled, backLabel = '← Cancelar' }) {
  return (
    <div className="pk-nav-btns">
      <button className="pk-btn-back" onClick={onCancelar}>{backLabel}</button>
      {onNext && (
        <button className="pk-btn-next" onClick={onNext} disabled={disabled}>Siguiente →</button>
      )}
    </div>
  )
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00A8B5;
  --cream: #F8FAFC;
  --line2: #e2e8f0;
  --muted2:#64748b;
}
html, body { background: var(--cream); margin: 0 }
.lp-app {
  min-height: 100vh; background: var(--cream);
  font-family: 'Inter', system-ui, sans-serif; color: var(--navy);
  -webkit-font-smoothing: antialiased; display: flex; flex-direction: column;
}

/* Navbar */
.lp-nav { position: sticky; top: 0; z-index: 50; background: rgba(26,52,97,.94); backdrop-filter: blur(12px) }
.lp-nav-inner { max-width: 1100px; margin: 0 auto; padding: 0 16px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 12px }
.lp-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; color: #fff }
.lp-logo-iso { width: 32px; height: 32px; background: #fff; border-radius: 8px; padding: 3px; box-sizing: border-box; object-fit: contain }
.lp-logo span { font-size: 22px; font-weight: 900; letter-spacing: -.02em }
.lp-logo em { font-style: normal; color: var(--teal) }
.lp-nav-actions { display: flex; gap: 10px; align-items: center }
.lp-nav-link { color: rgba(255,255,255,.78); text-decoration: none; font-size: 13px; font-weight: 600; padding: 8px 12px; border-radius: 999px }
.lp-nav-link:hover { background: rgba(255,255,255,.1); color: var(--teal) }
.lp-nav-logout { background: rgba(255,255,255,.12); color: #fff; border: 0; padding: 8px 14px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit }
.lp-nav-logout:hover { background: rgba(255,255,255,.2) }

/* Hero */
.pk-hero { background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00A8B5 100%); color: #fff; padding: 48px 16px 56px; text-align: center }
.pk-hero-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.18); padding: 6px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 14px }
.pk-hero-dot { width: 7px; height: 7px; background: var(--teal); border-radius: 50%; animation: pulse 1.6s infinite }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.pk-hero-h1 { font-size: clamp(28px, 5vw, 44px); font-weight: 900; line-height: 1.1; letter-spacing: -.02em; margin: 0 0 12px }
.pk-hero-h1 span { color: var(--teal) }
.pk-hero-sub { font-size: clamp(14px, 2.4vw, 16px); color: rgba(255,255,255,.82); max-width: 540px; margin: 0 auto }

/* Main */
.pk-main { flex: 1; max-width: 760px; width: 100%; margin: 0 auto; padding: 24px 16px 60px; box-sizing: border-box }

/* Stepper */
.pk-stepper { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; margin-bottom: 24px }
.pk-step { text-align: center; opacity: .45 }
.pk-step.active, .pk-step.done { opacity: 1 }
.pk-step-num { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%; background: #e2e8f0; color: #64748b; font-weight: 800; font-size: 13px; margin-bottom: 4px }
.pk-step.active .pk-step-num { background: var(--teal); color: var(--navy) }
.pk-step.done .pk-step-num { background: #22c55e; color: #fff }
.pk-step-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em }
.pk-step.active .pk-step-label { color: var(--navy) }

/* Segmentos */
.pk-segs { display: grid; grid-template-columns: 1fr; gap: 14px }
@media (min-width: 640px) { .pk-segs { grid-template-columns: 1fr 1fr } }
.pk-seg { background: #fff; border-radius: 20px; padding: 24px; text-align: left; border: 2px solid transparent; box-shadow: 0 4px 20px rgba(26,52,97,.06); cursor: pointer; transition: transform .18s, box-shadow .18s, border-color .18s; font-family: inherit; display: flex; flex-direction: column; gap: 6px }
.pk-seg:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(26,52,97,.12) }
.pk-seg.is-elegido { border-color: var(--teal); background: linear-gradient(180deg, #fff 0%, #f0fcfd 100%); box-shadow: 0 8px 28px rgba(0,196,212,.22) }
.pk-seg-icon { font-size: 36px; line-height: 1; margin-bottom: 4px }
.pk-seg-titulo { font-size: 18px; font-weight: 800; color: var(--navy) }
.pk-seg-sub { font-size: 13px; color: var(--teal); font-weight: 700 }
.pk-seg-det { font-size: 13px; color: var(--muted2); line-height: 1.5; margin-top: 4px }
.pk-seg-check { margin-top: 10px; align-self: flex-start; background: var(--teal); color: var(--navy); padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 800; letter-spacing: .03em }

/* Card */
.pk-card { background: #fff; border-radius: 20px; padding: 24px; box-shadow: 0 8px 40px rgba(26,52,97,.08); border: 1px solid rgba(26,52,97,.05) }

/* Tier toggle */
.pk-tier-info { background: #f0fcfd; border: 1px solid #b3eef3; border-radius: 12px; padding: 14px 16px; font-size: 13px; line-height: 1.55; color: var(--navy); margin-bottom: 18px }
.pk-toggle-wrap { display: flex; justify-content: center; margin-bottom: 22px }
.pk-toggle { background: #fff; border: 1px solid var(--line2); border-radius: 999px; padding: 5px; display: inline-flex; box-shadow: 0 2px 8px rgba(26,52,97,.04) }
.pk-toggle button { padding: 9px 22px; border-radius: 999px; border: 0; background: transparent; font-size: 12px; font-weight: 800; letter-spacing: .06em; color: rgba(26,52,97,.5); cursor: pointer; font-family: inherit }
.pk-toggle button.on { background: var(--navy); color: #fff }

/* Kits */
.pk-kits { display: flex; flex-direction: column; gap: 16px }
.pk-kit { position: relative; background: #fff; border: 2px solid var(--line2); border-radius: 18px; padding: 22px; transition: transform .18s, box-shadow .18s, border-color .18s }
.pk-kit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(26,52,97,.08) }
.pk-kit-badge { position: absolute; top: -10px; left: 18px; background: var(--teal); color: var(--navy); padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 800; letter-spacing: .03em }
.pk-kit-head { margin-bottom: 14px }
.pk-kit-titulo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px }
.pk-kit-titulo h3 { font-size: 20px; font-weight: 900; color: var(--navy); margin: 0; letter-spacing: -.01em }
.pk-kit-tier { font-size: 10px; font-weight: 800; letter-spacing: .06em; padding: 3px 10px; border-radius: 999px }
.tier-essential { background: #e8f5e9; color: #2e7d32 }
.tier-premium   { background: #e0f7fa; color: #006064 }
.tier-comercial { background: #fff3e0; color: #e65100 }
.pk-kit-meta { font-size: 12px; color: var(--muted2); font-weight: 600 }
.pk-kit-desc { font-size: 14px; color: #475569; line-height: 1.5; margin: 8px 0 0 }
.pk-kit-precio-row { display: flex; justify-content: space-between; align-items: center; gap: 14px; padding: 14px 0; border-top: 1px dashed var(--line2); border-bottom: 1px dashed var(--line2); margin-bottom: 14px }
.pk-kit-precio { font-size: 28px; font-weight: 900; color: var(--navy); letter-spacing: -.02em; line-height: 1 }
.pk-kit-precio-sub { font-size: 11px; color: var(--muted2); font-weight: 600; margin-top: 3px }
.pk-kit-ver { background: transparent; border: 0; font-size: 13px; font-weight: 700; color: var(--teal); cursor: pointer; padding: 8px 12px; border-radius: 8px; font-family: inherit; flex-shrink: 0 }
.pk-kit-ver:hover { background: #f0fcfd }
.pk-kit-productos { list-style: none; padding: 14px 16px; margin: 0 0 14px; background: #f8fafc; border-radius: 12px }
.pk-kit-productos li { font-size: 13px; padding: 6px 0; border-bottom: 1px solid var(--line2); display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px }
.pk-kit-productos li:last-child { border-bottom: 0 }
.pk-kit-prod-cant { font-weight: 800; color: var(--teal); min-width: 28px }
.pk-kit-prod-nom { font-weight: 600; color: var(--navy) }
.pk-kit-prod-pres, .pk-kit-prod-marca { font-size: 12px; color: var(--muted2) }
.pk-kit-elegir { width: 100%; padding: 14px 18px; border-radius: 999px; font-size: 14px; font-weight: 800; cursor: pointer; font-family: inherit; background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; box-shadow: 0 6px 18px rgba(0,196,212,.25); transition: transform .12s }
.pk-kit-elegir:hover { transform: translateY(-1px) }

.pk-link-todos { display: block; margin: 22px auto 0; padding: 10px 16px; background: transparent; border: 0; font-size: 13px; font-weight: 700; color: var(--blue); cursor: pointer; font-family: inherit }
.pk-link-todos:hover { text-decoration: underline }

/* Nav */
.pk-nav-btns { display: flex; justify-content: space-between; gap: 10px; margin-top: 24px; padding-top: 18px; border-top: 1px solid var(--line2) }
.pk-btn-back { padding: 12px 20px; background: transparent; border: 0; color: var(--muted2); font-weight: 700; font-size: 14px; cursor: pointer; border-radius: 999px; font-family: inherit }
.pk-btn-back:hover { background: #f1f5f9; color: var(--navy) }
.pk-btn-next { padding: 14px 22px; font-size: 14px; font-weight: 800; background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; border-radius: 999px; cursor: pointer; font-family: inherit; box-shadow: 0 6px 18px rgba(0,196,212,.25) }
.pk-btn-next:hover:not(:disabled) { transform: translateY(-1px) }
.pk-btn-next:disabled { opacity: .45; cursor: not-allowed; box-shadow: none }

/* CTA suscripción */
.pk-cta-susc { display: flex; align-items: center; gap: 16px; margin-top: 24px; padding: 18px 22px; background: linear-gradient(135deg, rgba(0,196,212,.08), rgba(26,92,184,.08)); border: 1px solid rgba(0,196,212,.25); border-radius: 14px; flex-wrap: wrap }
.pk-cta-susc > div { flex: 1; min-width: 220px }
.pk-cta-susc strong { color: var(--navy); font-size: 14px }
.pk-cta-susc p { color: #475569; font-size: 13px; margin: 6px 0 0; line-height: 1.5 }
.pk-cta-link { background: var(--navy); color: #fff !important; text-decoration: none; padding: 12px 18px; border-radius: 999px; font-weight: 800; font-size: 13px; white-space: nowrap }

/* Success */
.pk-success { flex: 1; display: flex; align-items: center; justify-content: center; padding: 60px 20px }
.pk-success-card { background: #fff; max-width: 460px; padding: 40px; border-radius: 24px; text-align: center; box-shadow: 0 20px 60px rgba(26,52,97,.15) }
.pk-success-icon { width: 70px; height: 70px; border-radius: 50%; background: linear-gradient(135deg, #22c55e, #10b981); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 38px; font-weight: 900; margin: 0 auto 20px }
.pk-success-card h1 { font-size: 28px; font-weight: 900; color: var(--navy); margin: 0 0 12px; letter-spacing: -.01em }
.pk-success-card p { font-size: 15px; color: #334155; margin: 8px 0 }
.pk-success-card .pk-success-msg { color: var(--muted2); font-size: 14px; line-height: 1.5; margin: 16px 0 24px }
.pk-success-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap }
.pk-btn-primary { background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; padding: 12px 22px; border-radius: 999px; text-decoration: none; font-weight: 800; font-size: 14px; box-shadow: 0 6px 18px rgba(0,196,212,.3) }
.pk-btn-ghost { background: #f1f5f9; color: var(--navy); padding: 12px 22px; border-radius: 999px; text-decoration: none; font-weight: 700; font-size: 14px }
`
